import React, { useRef } from "react";
import { useLocation, useOutlet } from "react-router-dom";
import { AuthProvider } from "../context/AuthContext";
import GoogleMapsLoader from "../components/GoogleMapsLoader";
import { ToastContainer } from "react-toastify";
import useGlobalImagePreloader from "../hooks/useGlobalImagePreloader";
import "react-toastify/dist/ReactToastify.css";
import { CookieConsentProvider } from "../context/CookieConsentContext";
import CookieBanner from "../components/CookieBanner";
import { Helmet } from "../lib/helmetCompat";

export default function RootLayout() {
	const location = useLocation();
	const outlet = useOutlet();
	const backgroundOutletRef = useRef(null);
	const origin =
		process.env.REACT_APP_CLIENT_URL ||
		(typeof window !== "undefined" ? window.location.origin : "");
	const currentUrl = `${origin}${location.pathname}${location.search}`;
	const isSettingsModalRoute =
		/^\/user\/[^/]+\/settings$/.test(location.pathname) &&
		Boolean(location.state?.backgroundLocation);

	if (!isSettingsModalRoute) {
		backgroundOutletRef.current = outlet;
	}

	// Inicializar precarga global de imágenes
	useGlobalImagePreloader({
		enabled: true,
		delay: 3000, // Esperar 3 segundos después del montaje inicial
		batchSize: 2, // Lotes pequeños para no interferir con la navegación
		batchDelay: 1000, // Pausa más larga entre lotes
	});

	return (
		<AuthProvider>
			<CookieConsentProvider>
				<GoogleMapsLoader>
					<Helmet>
						<title>Motorik</title>
						<meta
							name="description"
							content="Desde rutas y quedadas hasta competiciones, cursos y concentraciones. Todo el mundo del motor, en un solo lugar."
						/>
						<link rel="canonical" href={currentUrl} />
						<meta property="og:type" content="website" />
						<meta property="og:title" content="Motorik" />
						<meta
							property="og:description"
							content="Desde rutas y quedadas hasta competiciones, cursos y concentraciones. Todo el mundo del motor, en un solo lugar."
						/>
						<meta
							property="og:image"
							content="https://motorik.net/images/motorik-og-image.jpg"
						/>
						<meta property="og:url" content={currentUrl} />
						<meta name="twitter:card" content="summary_large_image" />
						<meta name="twitter:url" content={currentUrl} />
						<meta name="twitter:title" content="Motorik" />
						<meta
							name="twitter:description"
							content="Desde rutas y quedadas hasta competiciones, cursos y concentraciones. Todo el mundo del motor, en un solo lugar."
						/>
						<meta
							name="twitter:image"
							content="https://motorik.net/images/motorik-og-image.jpg"
						/>
					</Helmet>
					{isSettingsModalRoute && backgroundOutletRef.current
						? backgroundOutletRef.current
						: outlet}
					{isSettingsModalRoute && backgroundOutletRef.current ? outlet : null}
					<ToastContainer
						position="bottom-center"
						autoClose={5000}
						hideProgressBar={true}
						newestOnTop={false}
						closeOnClick={true}
						rtl={false}
						pauseOnFocusLoss={false}
						draggable={true}
						pauseOnHover={false}
						enableMultiContainer={false}
						limit={3}
						theme="light"
					/>
					<CookieBanner />
				</GoogleMapsLoader>
			</CookieConsentProvider>
		</AuthProvider>
	);
}
